import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Activity, AlertTriangle, Download, KeyRound, RefreshCw, Users, XCircle } from 'lucide-react';
import { useDarkMode } from '../../../context/DarkModeContext';
import Alert from '../Alert';
import {
  getFilterwerte, getKunden, getSchluessel, getUebersicht, getWebhooksStats,
  type FilterwerteDto, type KundenZeile, type SchluesselZeile, type UebersichtDto, type WebhookStatZeile,
} from '../../../lib/apiStatsApi';
import { alsAnfrage, ausParams, zuParams, type FilterZustand, type Sicht } from './filterZustand';
import FilterLeiste from './FilterLeiste';
import ZeitreiheChart from './ZeitreiheChart';
import KundenTabelle from './KundenTabelle';
import SchluesselTabelle from './SchluesselTabelle';
import WebhooksTabelle from './WebhooksTabelle';
import { deltaAnzeige, deltaFarbe, formatQuote, formatTag, formatZeitpunkt } from './format';
import { ladeCsvHerunter } from './csv';

const SICHTEN: Array<{ wert: Sicht; label: string }> = [
  { wert: 'zeit', label: 'Nach Zeit' },
  { wert: 'kunden', label: 'Nach Kunde' },
  { wert: 'schluessel', label: 'Nach Schlüssel' },
  { wert: 'webhooks', label: 'Webhooks' },
];

export default function ApiStatsTab() {
  const { darkMode } = useDarkMode();
  const [searchParams, setSearchParams] = useSearchParams();

  // Die Adresszeile IST der Zustand: wer den Link teilt, teilt den Blick.
  const filter = useMemo(() => ausParams(searchParams), [searchParams]);
  const anfrage = useMemo(() => alsAnfrage(filter), [filter]);
  const anfrageKey = JSON.stringify(anfrage);

  const setFilter = useCallback((f: FilterZustand) => {
    setSearchParams(zuParams(f), { replace: true });
  }, [setSearchParams]);

  const [uebersicht, setUebersicht] = useState<UebersichtDto | null>(null);
  const [kunden, setKunden] = useState<{ zeilen: KundenZeile[]; gekuerzt: boolean } | null>(null);
  const [schluessel, setSchluessel] = useState<{ zeilen: SchluesselZeile[]; gekuerzt: boolean } | null>(null);
  const [webhooks, setWebhooks] = useState<{ zeilen: WebhookStatZeile[]; gekuerzt: boolean } | null>(null);
  const [werte, setWerte] = useState<FilterwerteDto | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    setFehler(null);
    try {
      const [u, k, s, w] = await Promise.all([
        getUebersicht(anfrage),
        filter.sicht === 'kunden' ? getKunden(anfrage) : Promise.resolve(null),
        filter.sicht === 'schluessel' ? getSchluessel(anfrage) : Promise.resolve(null),
        filter.sicht === 'webhooks' ? getWebhooksStats(anfrage) : Promise.resolve(null),
      ]);
      setUebersicht(u);
      if (k) setKunden(k);
      if (s) setSchluessel(s);
      if (w) setWebhooks(w);
    } catch {
      setFehler('Statistik konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, [anfrageKey, filter.sicht]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => { getFilterwerte().then(setWerte).catch(() => setWerte(null)); }, []);

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const cardClass = `rounded-xl border ${card}`;
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const csvExport = () => {
    const name = `api-statistik_${anfrage.von}_${anfrage.bis}`;
    if (filter.sicht === 'zeit' && uebersicht) {
      ladeCsvHerunter(`${name}_zeit.csv`, ['Zeitpunkt', 'Aufrufe', 'Fehler'],
        uebersicht.zeitreihe.map(p => [p.zeitpunkt, p.aufrufe, p.fehler]));
    } else if (filter.sicht === 'kunden' && kunden) {
      ladeCsvHerunter(`${name}_kunden.csv`,
        ['Kunde', 'Aufrufe', 'Fehler', 'Fehlerquote', 'Schlüssel', 'Letzter Zugriff', 'Aufrufe Vorperiode'],
        kunden.zeilen.map(z => [z.mandant.name, z.aufrufe, z.fehler, formatQuote(z.fehlerquote), z.schluessel, formatZeitpunkt(z.letzterZugriff), z.vorherAufrufe]));
    } else if (filter.sicht === 'schluessel' && schluessel) {
      ladeCsvHerunter(`${name}_schluessel.csv`,
        ['Schlüssel', 'Präfix', 'Kunde', 'Stufe', 'Angelegt', 'Letzter Zugriff', 'Widerrufen', 'Aufrufe', 'Fehler', 'Fehlerquote', 'Aufrufe Vorperiode'],
        schluessel.zeilen.map(z => [z.name, `${z.prefix}…`, z.mandant.name, z.stufe, formatZeitpunkt(z.angelegt), formatZeitpunkt(z.letzterZugriff), z.widerrufen ? 'ja' : 'nein', z.aufrufe, z.fehler, formatQuote(z.fehlerquote), z.vorherAufrufe]));
    } else if (filter.sicht === 'webhooks' && webhooks) {
      ladeCsvHerunter(`${name}_webhooks.csv`,
        ['Ziel', 'Adresse', 'Kunde', 'Ereignisse', 'Status', 'Fehlschläge in Folge', 'Zustellungen', 'Fehlgeschlagen', 'Offen', 'Letzte Zustellung'],
        webhooks.zeilen.map(z => [z.ziel, z.url, z.mandant.name, z.events.join(', '), z.stillgelegt ? 'stillgelegt' : z.aktiv ? 'aktiv' : 'pausiert', z.failCount, z.zustellungen.gesamt, z.zustellungen.fehlgeschlagen, z.zustellungen.offen, formatZeitpunkt(z.letzteZustellung)]));
    }
  };

  const k = uebersicht?.kennzahlen;
  const v = uebersicht?.vorperiode;

  return (
    <div className="space-y-6">
      <FilterLeiste filter={filter} werte={werte} darkMode={darkMode} onChange={setFilter} />

      {fehler && (
        <Alert type="error">
          {fehler}
          <button onClick={() => void laden()} className="ml-2 text-xs underline">Neu versuchen</button>
        </Alert>
      )}

      {uebersicht?.zeitraum.gekuerzt && (
        <Alert type="warning">Der gewählte Zeitraum wurde auf 400 Tage gekürzt.</Alert>
      )}

      {/* Kennzahlen mit Veränderung zur Vorperiode */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Activity} label="Aufrufe" wert={String(k?.aufrufe ?? 0)}
          delta={k && v ? deltaAnzeige(k.aufrufe, v.aufrufe) : null} cardClass={cardClass} muted={muted} darkMode={darkMode} />
        <Kpi icon={XCircle} label="Fehler" wert={`${k?.fehler ?? 0} (${formatQuote(k?.fehlerquote ?? 0)})`}
          delta={k && v ? deltaAnzeige(k.fehler, v.fehler) : null} cardClass={cardClass} muted={muted} darkMode={darkMode} />
        <Kpi icon={Users} label="Kunden mit Zugriff" wert={String(k?.kunden ?? 0)}
          delta={k && v ? deltaAnzeige(k.kunden, v.kunden) : null} cardClass={cardClass} muted={muted} darkMode={darkMode} />
        <Kpi icon={KeyRound} label="Aktive Schlüssel" wert={String(k?.aktiveSchluessel ?? 0)}
          delta={k && v ? deltaAnzeige(k.aktiveSchluessel, v.aktiveSchluessel) : null} cardClass={cardClass} muted={muted} darkMode={darkMode} />
      </div>

      {/* Auffälligkeiten — kompakt, nicht als Leitmotiv */}
      {(uebersicht?.auffaelligkeiten.length ?? 0) > 0 && (
        <div className="flex flex-wrap gap-2">
          {uebersicht!.auffaelligkeiten.map((a, i) => (
            <span key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-800'
            }`}>
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {a.text}
            </span>
          ))}
        </div>
      )}

      {/* Sicht-Umschalter + Werkzeuge */}
      <div className="flex flex-wrap items-center gap-3">
        <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-slate-700' : 'border-slate-300'}`}>
          {SICHTEN.map(s => (
            <button
              key={s.wert}
              type="button"
              onClick={() => setFilter({ ...filter, sicht: s.wert })}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                filter.sicht === s.wert
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <button
          onClick={() => void laden()}
          disabled={laedt}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
            darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${laedt ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>

        <button
          onClick={csvExport}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
            darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          CSV
        </button>

        <span className={`text-xs ${muted}`}>
          {formatTag(anfrage.von)} – {formatTag(anfrage.bis)}
        </span>
      </div>

      {/* Die gewählte Sicht */}
      {filter.sicht === 'zeit' && (
        <div className={`${cardClass} p-4`}>
          <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Verlauf ({uebersicht?.aufloesung === 'stunde' ? 'stündlich' : uebersicht?.aufloesung === 'woche' ? 'wochenweise' : 'täglich'})
          </h3>
          {uebersicht && (
            <ZeitreiheChart punkte={uebersicht.zeitreihe} aufloesung={uebersicht.aufloesung} darkMode={darkMode} />
          )}
        </div>
      )}

      {filter.sicht === 'kunden' && kunden && (
        <div className={cardClass}>
          <KundenTabelle
            zeilen={kunden.zeilen}
            gekuerzt={kunden.gekuerzt}
            darkMode={darkMode}
            onWaehleMandant={wert => setFilter({ ...filter, mandanten: [wert] })}
          />
        </div>
      )}

      {filter.sicht === 'schluessel' && schluessel && (
        <div className={cardClass}>
          <SchluesselTabelle
            zeilen={schluessel.zeilen}
            gekuerzt={schluessel.gekuerzt}
            anfrage={anfrage}
            darkMode={darkMode}
            onEndpunktWaehlen={route => setFilter({ ...filter, endpunkt: route })}
          />
        </div>
      )}

      {filter.sicht === 'webhooks' && webhooks && (
        <div className={cardClass}>
          <WebhooksTabelle zeilen={webhooks.zeilen} gekuerzt={webhooks.gekuerzt} darkMode={darkMode} />
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, wert, delta, cardClass, muted, darkMode }: {
  icon: typeof Activity;
  label: string;
  wert: string;
  delta: { text: string; richtung: 'auf' | 'ab' | 'gleich' | 'neu' } | null;
  cardClass: string;
  muted: string;
  darkMode: boolean;
}) {
  return (
    <div className={`${cardClass} p-4`}>
      <div className={`flex items-center gap-1.5 text-[12px] uppercase tracking-wide ${muted}`}>
        <Icon className="w-3 h-3" />{label}
      </div>
      <p className="text-2xl font-bold mt-1">{wert}</p>
      {delta && (
        <p className={`text-xs mt-0.5 ${deltaFarbe(delta.richtung, darkMode)}`} title="Veränderung zur Vorperiode">
          {delta.text} ggü. Vorperiode
        </p>
      )}
    </div>
  );
}
